import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { TabBar } from "@/components/toggles";
import { ArrowLeft, Package, QrCode, Truck, CheckCircle, MessageCircle, Sparkles, ExternalLink, Star } from "lucide-react";

/**
 * mode: "buyer" (Käufe) | "seller" (Verkäufe) | "both" (Legacy Tab-View)
 * Owner-Vorgabe: Studio zeigt NUR Verkäufe, Profil zeigt NUR Käufe → getrennte
 * URLs (/profile/sales bzw. /profile/purchases) rendern denselben Component
 * mit fest verdrahtetem `mode`.
 */
export default function OrdersPage({ mode = "both" }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(mode === "seller" ? "seller" : "buyer");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewedMap, setReviewedMap] = useState({}); // order_id → true/false

  const STATUS_MAP = {
    CREATED: { label: t("orders.status_created"), color: "text-amber-500", desc: t("orders.desc_created") },
    PAYMENT_PENDING: { label: t("orders.status_payment_pending"), color: "text-amber-500", desc: t("orders.desc_payment_pending") },
    PAID: { label: t("orders.status_paid"), color: "text-green-600", desc: t("orders.desc_paid") },
    AWAITING_INBOUND: { label: t("orders.status_awaiting_inbound"), color: "text-amber-500", desc: t("orders.desc_awaiting_inbound") },
    SHIPPED: { label: t("orders.status_shipped"), color: "text-blue-600", desc: t("orders.desc_shipped") },
    DELIVERED: { label: t("orders.status_delivered"), color: "text-green-600", desc: t("orders.desc_delivered") },
    COMPLETED: { label: t("orders.status_completed"), color: "text-[#999]", desc: t("orders.desc_completed") },
    CANCELLED: { label: t("orders.status_cancelled"), color: "text-red-500", desc: t("orders.desc_cancelled") },
    REFUNDED: { label: t("orders.status_refunded"), color: "text-[#999]", desc: t("orders.desc_refunded") },
  };

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    loadOrders();
  }, [user, navigate, tab]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const endpoint = tab === "buyer" ? "/orders/buyer" : "/orders/seller";
      const { data } = await api.get(endpoint);
      const list = data.orders || [];
      setOrders(list);
      // Für Buyer: Review-Status parallel laden
      if (tab === "buyer" && list.length > 0) {
        const reviewable = list.filter((o) => ["DELIVERED", "RECEIVED", "COMPLETED"].includes(o.status || o.order_status));
        const map = {};
        await Promise.all(reviewable.map(async (o) => {
          try {
            const { data: r } = await api.get(`/reviews/for-order/${o.order_id}`);
            map[o.order_id] = !!r.review;
          } catch { /* noop */ }
        }));
        setReviewedMap(map);
      }
    } catch { setOrders([]); }
    finally { setLoading(false); }
  };

  const markShipped = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/ship`);
      loadOrders();
    } catch { /* ignore */ }
  };

  const pageTitle = mode === "seller"
    ? t("orders.title_sales")
    : (mode === "buyer" ? t("orders.title_purchases") : t("orders.title_default"));

  return (
    <div data-testid="orders-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="orders-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">{pageTitle}</h1>
      </div>

      {mode === "both" && (
        <TabBar
          testId="orders-tabs"
          tabs={[
            { key: "buyer", label: t("orders.tab_purchases"), testId: "orders-tab-buyer" },
            { key: "seller", label: t("orders.tab_sales"), testId: "orders-tab-seller" },
          ]}
          activeKey={tab}
          onChange={setTab}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <Package size={32} className="mx-auto text-[#E5E5E5] mb-3" />
          <p className="text-sm text-[#999]">{tab === "buyer" ? t("orders.empty_purchases") : t("orders.empty_sales")}</p>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {orders.map((order) => {
            const s = STATUS_MAP[order.status] || { label: order.status, color: "text-[#999]", desc: "" };
            const canOpenDetail = true; // sowohl Buyer als auch Seller können Detail öffnen
            const isReviewable = ["DELIVERED", "RECEIVED", "COMPLETED"].includes(order.status);
            const alreadyReviewed = reviewedMap[order.order_id];
            const showReviewBadge = tab === "buyer" && isReviewable && !alreadyReviewed;
            const RowTag = "div";
            const detailPath = tab === "buyer"
              ? `/profile/purchases/${order.order_id}`
              : `/profile/sales/${order.order_id}`;
            const rowExtra = canOpenDetail ? {
              onClick: () => navigate(detailPath),
              role: "button",
              tabIndex: 0,
              className: "px-4 py-4 hover:bg-[#F9F9F9] transition-colors cursor-pointer",
            } : { className: "px-4 py-4" };
            return (
              <RowTag key={order.order_id} data-testid={`order-${order.order_id}`} {...rowExtra}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A0A0A] truncate flex items-center gap-1.5">
                      {order.sale_mode === "MYSTERY_BAG" && <Sparkles size={12} className="text-amber-500 shrink-0" />}
                      {order.title}
                    </p>
                    <p className="text-xs text-[#999] mt-0.5">{new Date(order.created_at).toLocaleDateString(i18n.language)}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className={`text-xs font-medium ${s.color}`}>{s.label}</p>
                      {showReviewBadge && (
                        <span
                          data-testid={`order-review-badge-${order.order_id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[9px] font-bold uppercase tracking-wider">
                          <Star size={9} /> {t("orders.review_cta")}
                        </span>
                      )}
                      {alreadyReviewed && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-green-700">
                          <Star size={10} className="fill-green-700" /> {t("orders.reviewed")}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-[#CCC] mt-0.5">{s.desc}</p>
                  </div>
                  <p className="text-sm font-bold text-[#0A0A0A] shrink-0">{order.price?.toFixed(2)} {order.currency}</p>
                </div>

                {/* F-4 Feinschliff: Mystery-Bag Aufschlüsselung (Anonymität) */}
                {Array.isArray(order.mystery_items) && order.mystery_items.length > 0 && (
                  <div data-testid={`order-mystery-items-${order.order_id}`} className="mt-3 pt-3 border-t border-[#F0F0F0] space-y-1">
                    <p className="text-[10px] uppercase tracking-wider text-[#999] font-medium">{t("orders.included")}</p>
                    {order.mystery_items.map((it, i) => (
                      <div key={it.item_id} data-testid={`order-mystery-item-${order.order_id}-${i}`} className="flex items-center gap-2 text-[11px]">
                        <span className="text-[#666]">{it.wear_duration_days}T ·</span>
                        <span
                          data-testid={`order-mystery-seller-${order.order_id}-${i}`}
                          className="text-[#666]"
                        >{it.seller_display_name || t("orders.anonymous")}</span>
                        <span className="ml-auto font-mono text-[#999]">{Number(it.rescue_price || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* iter79 M5: Shipment-Tracking-Block pro Order (Multi-Seller natürlich getrennt) */}
                {(order.tracking_number || order.tracking_url || order.label_prepurchase_carrier) && (
                  <div data-testid={`order-tracking-${order.order_id}`} className="mt-3 pt-3 border-t border-[#F0F0F0]">
                    <p className="text-[10px] uppercase tracking-wider text-[#999] font-medium mb-1.5">{t("orders.shipping")}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {(order.tracking_carrier || order.label_prepurchase_carrier) && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F5F5F5] border border-[#E5E5E5] text-[10px] text-[#666]">
                          <Truck size={10} /> {order.tracking_carrier || order.label_prepurchase_carrier}
                        </span>
                      )}
                      {order.tracking_number && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F5F5F5] border border-[#E5E5E5] text-[10px] text-[#666] font-mono">
                          {order.tracking_number}
                        </span>
                      )}
                      {order.tracking_url && (
                        <a href={order.tracking_url} target="_blank" rel="noopener noreferrer"
                          data-testid={`order-tracking-link-${order.order_id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#0A0A0A] text-white text-[10px]">
                          {t("orders.track_shipment")} <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Seller Actions */}
                {tab === "seller" && order.status === "PAID" && (
                  <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                    {order.qr_token && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F5F5F5] border border-[#E5E5E5] text-xs text-[#666]">
                        <QrCode size={12} /> {t("orders.ship_code")}: {order.qr_token.slice(0, 8)}...
                      </div>
                    )}
                    <button data-testid={`ship-${order.order_id}`} onClick={(e) => { e.stopPropagation(); markShipped(order.order_id); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222] transition-colors active:scale-[0.98]">
                      <Truck size={12} /> {t("orders.mark_shipped")}
                    </button>
                  </div>
                )}

                {tab === "seller" && order.status === "SHIPPED" && (
                  <div className="mt-2 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <CheckCircle size={12} /> {t("orders.shipped")}
                    </div>
                    <button data-testid={`chat-${order.order_id}`} onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.order_id}/chat`); }}
                      className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] text-[#666] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
                      <MessageCircle size={10} /> {t("orders.chat")}
                    </button>
                  </div>
                )}

                {/* Buyer chat link */}
                {tab === "buyer" && order.status === "SHIPPED" && (
                  <div className="mt-2">
                    <button data-testid={`buyer-chat-${order.order_id}`}
                      onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.order_id}/chat`); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#0A0A0A] border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors">
                      <MessageCircle size={12} /> {t("orders.open_chat")}
                    </button>
                  </div>
                )}
              </RowTag>
            );
          })}
        </div>
      )}
    </div>
  );
}
